import { useCallback } from 'react';
import { getDataTableColumns } from '../cols-definition';
import { useDynamicSearch } from '@/hooks';
import { QUERY_KEYS } from '@/constants';

interface UseSearchTableProps {
  searchData: (filters: Record<string, unknown>, page?: number, searchPageSize?: number) => Promise<void>;
  isFiltered: boolean;
  handlePageChange: (page: number) => Promise<void>;
}

export const useSearchTable = ({ searchData, isFiltered, handlePageChange }: UseSearchTableProps) => {
  const onSearch = useCallback(
    async (params: Record<string, unknown>) => {
      await searchData(params);
    },
    [searchData]
  );

  const onInitialSearch = useCallback(
    async (formData: Record<string, unknown>, onSearchFn: (params: Record<string, unknown>) => Promise<void>) => {
      await onSearchFn(formData);
    },
    []
  );

  const getColumns = useCallback(() => {
    const cols = getDataTableColumns();
    return cols.map(col => ({
      field: col.field,
      type: col.type
    }));
  }, []);

  const {
    searchState,
    handleSearchChange,
    handleSearchPageChange,
    hasFilterParams,
    handleInitialSearch,
    handleRefreshClick,
    isSearching
  } = useDynamicSearch({
    onSearch,
    params: {},
    onInitialSearch,
    getColumns,
    endpoint: `${QUERY_KEYS.HAN_THANH_TOAN}/filter`,
    pageSize: 20
  });

  const onPageChange = useCallback(
    async (page: number) => {
      if (hasFilterParams) {
        await handleSearchPageChange(page);
      } else if (isFiltered) {
        await searchData({}, page);
      } else {
        await handlePageChange(page);
      }
    },
    [hasFilterParams, handleSearchPageChange, isFiltered, searchData, handlePageChange]
  );

  return {
    searchState,
    handleSearchChange,
    onPageChange,
    hasFilterParams,
    handleInitialSearch,
    handleRefreshClick,
    isFiltering: isSearching
  };
};
